import { combine, createEffect, createEvent, createStore } from 'effector';
import { PaginationProps } from 'antd/lib/pagination';
import { get } from 'lodash';

import api from '@constants/endpoints';
import request from '@helpers/request';
import IBlockedEmailDomain, {
  BlockedEmailDomainStatus,
} from '@interfaces/IBlockedEmailDomain';
import createFiltersStore from './filters';
import createPaginationStore from './pagination';
import createPreloadersStore from './preloaders';

export type BlockedEmailDomainsSort =
  | 'STATUS_ASC'
  | 'STATUS_DESC'
  | 'UPDATED_AT_ASC'
  | 'UPDATED_AT_DESC';

interface BlockedEmailDomainsFilters {
  domain?: string;
  sort?: BlockedEmailDomainsSort;
}

export const reset = createEvent('Blocked email domains store reset');

const defaultFilters: BlockedEmailDomainsFilters = {
  domain: undefined,
  sort: undefined,
};

export const getList = createEffect({
  name: 'getBlockedEmailDomains',
  async handler(params?: {
    pagination?: PaginationProps;
    filters?: BlockedEmailDomainsFilters;
    sort?: BlockedEmailDomainsSort;
  }) {
    const paginationParams = {
      ...pagination.getState(),
      ...params?.pagination,
    };
    let filtersParams = filters.getState() as BlockedEmailDomainsFilters;

    if (params?.filters) {
      filtersParams = params.filters;
      setFilters(filtersParams);
      paginationParams.current = 1;
    }

    if (params && Object.prototype.hasOwnProperty.call(params, 'sort')) {
      filtersParams = { ...filtersParams, sort: params.sort };
      setFilters(filtersParams);
    }

    const response = await request(api.blockedEmailDomains.search, {
      data: {
        pagination: {
          type: 'PAGE',
          page: (paginationParams.current || 1) - 1,
          pageSize: Number(
            paginationParams.pageSize ??
              localStorage.getItem('blockedEmailDomainsPageSize') ??
              10,
          ),
        },
        filter: filtersParams.domain ? { domain: filtersParams.domain.trim() } : {},
        ...(filtersParams.sort ? { sort: filtersParams.sort } : {}),
      },
    });

    return response;
  },
});

export const createDomain = createEffect({
  name: 'createBlockedEmailDomain',
  async handler({ domain }: { domain: string }) {
    return request(api.blockedEmailDomains.create, {
      data: { domain: domain.trim().toLowerCase() },
    });
  },
});

export const updateStatus = createEffect({
  name: 'updateBlockedEmailDomainStatus',
  async handler({
    domain,
    status,
  }: {
    domain: string;
    status: BlockedEmailDomainStatus;
    preloaderId?: string;
  }) {
    return request(api.blockedEmailDomains.updateStatus, {
      data: { domain, status },
    });
  },
});

const { pagination, setPagination } = createPaginationStore({
  getList,
  listName: 'blockedEmailDomains',
  resetEvent: reset,
  bodyPath: ['data', 'body'],
});

export { setPagination };

const { filters, setFilters } = createFiltersStore({
  listName: 'blockedEmailDomains',
  deaultValues: defaultFilters,
  resetEvent: reset,
});

export { setFilters };

const preloaders = createPreloadersStore([getList, createDomain, updateStatus]);

const readList = (result: any): IBlockedEmailDomain[] => {
  const body = get(result, ['data', 'body'], {});
  if (Array.isArray(body.page)) return body.page;
  if (Array.isArray(body.content)) return body.content;
  if (Array.isArray(body.items)) return body.items;
  return [];
};

const list = createStore<IBlockedEmailDomain[]>([])
  .on(getList.done, (_, { result }) => readList(result))
  .on(createDomain.done, () => {
    getList();
  })
  .on(updateStatus.done, () => {
    getList();
  })
  .reset(reset);

export default combine({
  list,
  filters,
  pagination,
  preloaders,
});
